import { supabase } from './supabase';

export interface AvatarUploadResult {
  url: string;
  path: string;
}

export class AvatarService {
  private static readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  private static readonly ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  /**
   * Generate a default avatar URL using initials
   */
  static generateDefaultAvatar(name: string, email?: string): string {
    const displayName = name || email?.split('@')[0] || 'User';
    const initials = displayName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');

    // Generate a consistent color based on the name
    const colors = [
      '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316',
      '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981',
      '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1'
    ];
    
    const colorIndex = displayName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    const backgroundColor = colors[colorIndex];

    // Create SVG avatar with initials
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
        <circle cx="64" cy="64" r="64" fill="${backgroundColor}"/>
        <text x="64" y="74" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="48" font-weight="600">
          ${initials}
        </text>
      </svg>
    `.trim();

    // Convert SVG to data URL
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }

  /**
   * Upload avatar to Supabase Storage
   */
  static async uploadAvatar(file: File, userId: string): Promise<AvatarUploadResult> {
    try {
      // Validate file
      this.validateFile(file);

      // Create file path
      const fileExtension = file.name.split('.').pop();
      const fileName = `avatar-${Date.now()}.${fileExtension}`;
      const filePath = `${userId}/${fileName}`;

      // Upload to Supabase Storage
      const { error } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        throw new Error(`Avatar upload failed: ${error.message}`);
      }

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      return {
        url: publicUrlData.publicUrl,
        path: filePath
      };

    } catch (error) {
      console.error('Avatar upload error:', error);
      throw error;
    }
  }

  /**
   * Delete old avatar from storage
   */
  static async deleteAvatar(filePath: string): Promise<void> {
    try {
      if (!filePath) return;

      const { error } = await supabase.storage
        .from('avatars')
        .remove([filePath]);

      if (error) {
        console.error('Error deleting old avatar:', error);
        // Don't throw error as this is not critical
      }
    } catch (error) {
      console.error('Avatar deletion error:', error);
      // Don't throw error as this is not critical
    }
  }

  /**
   * Validate avatar file
   */
  private static validateFile(file: File): void {
    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error(`Avatar file size must be less than ${this.MAX_FILE_SIZE / (1024 * 1024)}MB`);
    }

    // Check file type
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      throw new Error('Avatar must be a JPEG, PNG, GIF, or WebP image');
    }
  }

  /**
   * Get avatar URL with fallback to default
   */
  static getAvatarUrl(avatar: string | null, name?: string, email?: string): string {
    if (avatar && !avatar.includes('pexels.com')) {
      return avatar;
    }
    
    // Generate default avatar for users without avatar or with old default
    return this.generateDefaultAvatar(name || 'User', email);
  }
}

export default AvatarService;